import { Button, IconButton, Typography } from "@material-ui/core";
import Slider from "@material-ui/core/Slider";
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import FullscreenExitIcon from "@material-ui/icons/FullscreenExit";
import PauseIcon from "@material-ui/icons/Pause";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import VideocamOutlinedIcon from "@material-ui/icons/VideocamOutlined";
import Skeleton from "@material-ui/lab/Skeleton";
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import { useTranslation } from "react-i18next";

import client from "@/api/client";
import { AlertTileContext } from "@/common/contexts/AlertTileContextType";
import { toMMSS } from "@/utilities/alerts";
import { CameraAlertMedia } from "@/utilities/types/alert";
import MediaErrorPlaceholder from "@/views/alerts/AlertsPage/AlertItem/AlertClip/MediaErrorPlaceholder/MediaErrorPlaceholder";

import useStyles from "./AlertMediaTile.styles";


interface AlertMediaTileProps {
    datum: CameraAlertMedia;
    onClose?: () => void;
}

export const AlertMediaTile: FC<AlertMediaTileProps> = ({ datum }) => {
    const { primaryAlertTimestamp, currentAlertTimestamp,shouldClipsPlay, setShouldClipsPlay, clipProgress,setClipProgress, isSeeking, setIsSeeking } = useContext(AlertTileContext);
    const { t } = useTranslation("translation", { keyPrefix: "components.alerts.alertMediaTile" });
    const handle = useFullScreenHandle();
    const videoElementRef = useRef<HTMLVideoElement>(null);
    const classes = useStyles();
    const [showControls, setShowControls] = useState<boolean>(false);
    const [isFullScreen, setIsFullScreen] = useState<boolean>(false);

    //If video is ready on the server. (Get request to this resource returns a 200)
    const [isVideoReady, setIsVideoReady] = useState<boolean>(Boolean(datum.media.videoMedia));

    //If image is ready on the server. (Get request to this resource returns a 200)
    const [isImageReady, setIsImageReady] = useState<boolean>(true);
    //Try to check if videos and/images are available for 5 times (each time is 5000 ms)
    const noOfAttempts = 5;

    const [noOfAttemptsImage, setNoOfAttemptsImage] = useState<number>(noOfAttempts);
    const [noOfAttemptsVideo, setNoOfAttemptsVideo] = useState<number>(noOfAttempts);

    //Should show video only for primary alerts
    const shouldShowVideo = useMemo(()=>{
        return primaryAlertTimestamp === currentAlertTimestamp;
    },[primaryAlertTimestamp, currentAlertTimestamp]);

    useEffect(() => {
        if (!shouldShowVideo) return;

        if (isVideoReady ) return;

        if (noOfAttemptsVideo === 0) return;

        const checkVideoUrl = ()=>{
            if (datum.media.videoMedia) {
                //Check if the video is available
                client.get(datum.media.videoMedia.url).then((res)=>{
                        if (res.status === 200) {
                            setIsVideoReady(true);
                            setNoOfAttemptsVideo(noOfAttempts);
                        }
                }).catch(()=>{ //Set interval to keep trying
                    setIsVideoReady(false);
                    setNoOfAttemptsVideo(noOfAttemptsVideo-1);
                });
            }
        };
        //TODO: Figure out setInterval
        const id = setInterval(checkVideoUrl,5000);
        // ASK

        checkVideoUrl();

        return ()=>clearInterval(id);
    }, [noOfAttempts, noOfAttemptsVideo, isVideoReady, shouldShowVideo]);

    useEffect(() => {
        if (isImageReady || isVideoReady ) return;

        if (noOfAttemptsImage === 0) return;

        const checkImageUrl = ()=>{
            if(datum.media.imageMedia) {
                //Check if the video is available
                client.get(datum.media.imageMedia.url).then((res)=>{
                    if (res.status === 200) {
                        setIsImageReady(true);
                        setNoOfAttemptsImage(noOfAttempts);
                    }
                }).catch(()=>{ //Set interval to keep trying
                    setIsImageReady(false);
                    setNoOfAttemptsImage(noOfAttemptsImage-1);
                });
            }
        };
        //TODO: Figure out setInterval
        const id = setInterval(checkImageUrl,5000);
        // ASK

        checkImageUrl();

        return ()=>clearInterval(id);
    }, [noOfAttempts,noOfAttemptsImage, isImageReady, isVideoReady]);

    useEffect(() => {
        if (videoElementRef.current) {
            shouldClipsPlay ? videoElementRef.current.play() : videoElementRef.current.pause();
        }
    }, [shouldClipsPlay, videoElementRef]);

    const totalDuration = videoElementRef.current?.duration || 0;

    useEffect(()=>{
        if (!isSeeking) return;

        if (videoElementRef.current) {
            videoElementRef.current.currentTime = clipProgress;
        }
    }, [clipProgress, isSeeking]);

    const handleOnTimeUpdate = useCallback(() => {
        if (videoElementRef.current) {
            const currentTime = videoElementRef.current.currentTime;

            setClipProgress(currentTime);

        }
    }, [videoElementRef, clipProgress]);

    const cameraRole = datum.media.imageMedia ? datum.media.imageMedia.cameraRole : undefined;

    // How do you know how long to wait for video
    return(<FullScreen
        className={classes.wrapper}
        handle={handle}
        >
        {
        (shouldShowVideo && isVideoReady &&
            datum.media.videoMedia
            )
            &&
                <div className={classes.wrapper}>
                    <video
                        height={"100%"}
                        width={"100%"}
                        autoPlay
                        ref={videoElementRef}
                        src={datum.media.videoMedia.url}
                        onTimeUpdate={handleOnTimeUpdate}
                        loop
                        muted
                        onError={()=>setIsVideoReady(false)}
                    />

                    {<div
                        className={classes.controlsWrapper}
                        onMouseEnter={()=>
                            setShowControls(true)}
                        onMouseLeave={()=> setShowControls(false)}>
                        <div className={classes.controls}>
                            <div className={classes.controlsTop}>
                                {cameraRole ? <Button
                                    disabled
                                    className={classes.cameraRole}
                                    variant="contained"
                                    size="medium"
                                    startIcon={<VideocamOutlinedIcon className={classes.cameraRoleIcon} />}
                                >
                                    {t(`cameraRole.${cameraRole}`, cameraRole)}
                                </Button> : <Skeleton className={classes.cameraRole}/>}
                                {showControls && (isFullScreen
                                    ?
                                        <IconButton className={classes.controlButton} aria-label="fullscreenexit" onClick={()=>{ handle.exit();setIsFullScreen(false);}}>
                                            <FullscreenExitIcon fontSize="medium"/>
                                        </IconButton>
                                    :
                                        <IconButton className={classes.controlButton} aria-label="fullscreen" onClick={()=>{ handle.enter();setIsFullScreen(true);}}>
                                            <FullscreenIcon fontSize="medium"/>
                                        </IconButton>
                                )}
                            </div>
                            {showControls && <div className={classes.controlsBottom}>
                                <div className={classes.playAndTime}>
                                    <IconButton className={classes.controlButton} aria-label="play" onClick={()=>setShouldClipsPlay(!shouldClipsPlay)}>
                                        {shouldClipsPlay ? <PauseIcon fontSize="medium"/> : <PlayArrowIcon fontSize="medium"/>}
                                    </IconButton>
                                    <Typography className={classes.controlText}>
                                        {`${toMMSS(clipProgress)}/${toMMSS(totalDuration)}`}
                                    </Typography>
                                </div>
                                <Slider
                                    className={classes.slider}
                                    min={0}
                                    max={totalDuration}
                                    value={clipProgress}
                                    aria-labelledby="seeker"
                                    onChange={(_e: any, newValue: any) => {
                                        setIsSeeking(true);

                                        if(videoElementRef.current) {
                                            setClipProgress(newValue);
                                        }
                                    }}
                                    onChangeCommitted={(_e: any, newValue: any)=>{
                                        if(videoElementRef.current) {
                                            setClipProgress(newValue);
                                        }

                                        setIsSeeking(false);
                                    }}
                                />
                            </div>}
                        </div>
                    </div>}
                </div>
        }
        {(!shouldShowVideo || !isVideoReady) && isImageReady
        && datum.media.imageMedia &&
        <div className={classes.wrapper}>
            <img
                height={"100%"}
                width={"100%"}
                src={datum.media.imageMedia.url}
                onError={()=>setIsImageReady(false)}
            />
            {<div
                className={classes.controlsWrapper}
                onMouseEnter={()=>
                    setShowControls(true)}
                onMouseLeave={()=> setShowControls(false)}>
                <div className={classes.controls}>
                    <div className={classes.controlsTop}>
                        {cameraRole ? <Button
                            disabled
                            className={classes.cameraRole}
                            variant="contained"
                            size="medium"
                            startIcon={<VideocamOutlinedIcon className={classes.cameraRoleIcon} />}
            >
                            {t(`cameraRole.${cameraRole}`, cameraRole)}
                        </Button> :
                        <Skeleton className={classes.cameraRole}/>}
                        {showControls && (isFullScreen
                            ?
                                <IconButton className={classes.controlButton} aria-label="fullscreenexit" onClick={()=>{ handle.exit();setIsFullScreen(false);}}>
                                    <FullscreenExitIcon fontSize="medium"/>
                                </IconButton>
                            :
                                <IconButton className={classes.controlButton} aria-label="fullscreen" onClick={()=>{ handle.enter();setIsFullScreen(true);}}>
                                    <FullscreenIcon fontSize="medium"/>
                                </IconButton>
                        )}
                    </div>
                </div>
            </div>}
        </div>
        }
        {!isVideoReady &&
        !isImageReady &&
        <div style={{ height: "100%",
                    width: "100%" }}>
            <MediaErrorPlaceholder/>
        </div>
}
    </FullScreen>);
};
